import { ChangeEvent } from 'react';
import { formatInteger } from 'utils/format';
import { isNumber } from 'utils/validate';

/**
 * All functions in this file are to be used with react-hook-form.
 * Example: middleware functions for value transformation.
 */

/**
 * Removes commas from a formatted number string.
 * @param value The formatted string value (e.g., "1,000,000")
 * @returns The numeric string without commas (e.g., "1000000")
 */
export const removeCommas = (value: string): string => {
  return value.replace(/,/g, '');
};

/**
 * Formats a number string with commas for display.
 * @param value The numeric string value (e.g., "1000000")
 * @returns The formatted string with commas (e.g., "1,000,000")
 */
export const formatNumberWithCommas = (value: string | number): string => {
  if (!value && value !== 0) {
    return '';
  }
  const numValue = typeof value === 'string' ? Number(removeCommas(value)) : value;
  if (!isNumber(numValue) || numValue < 0) {
    return '';
  }

  return formatInteger(Math.floor(numValue));
};

/**
 * Formats a float number with commas for the integer part.
 * @param value The numeric value (e.g., 1234.56)
 * @returns The formatted string with commas in integer part (e.g., "1,234.56")
 */
export const formatFloatWithCommas = (value: string | number): string => {
  if (!value && value !== 0) {
    return '';
  }
  const numValue = typeof value === 'string' ? Number(removeCommas(value)) : value;
  if (!isNumber(numValue) || numValue < 0) {
    return '';
  }

  const parts = String(numValue).split('.');
  const integerPart = parts[0] || '0';
  const decimalPart = parts[1] || '';

  const formattedInteger = formatInteger(Number(integerPart));

  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

/* Transforms a textInput to a string representation of an integer value.
 * This is NOT a pure function.
 * This WILL transform the provided event param.
 */
export const textInputToIntegerMiddleware = (e: ChangeEvent<HTMLInputElement>) => {
  const enteredValue = Number(e.target.value);
  if (!isNumber(enteredValue) || enteredValue < 0) {
    e.target.value = '0';

    return e;
  }

  e.target.value = String(Math.floor(enteredValue));

  return e;
};

/* Transforms a textInput to a formatted integer value with commas.
 * This is NOT a pure function.
 * This WILL transform the provided event param.
 */
export const textInputToFormattedIntegerMiddleware = (
  e: ChangeEvent<HTMLInputElement>,
) => {
  // Remove commas to get the raw numeric value
  const rawValue = removeCommas(e.target.value);

  // If empty, allow it (user might be deleting)
  if (rawValue === '') {
    e.target.value = '';

    return e;
  }

  const enteredValue = Number(rawValue);

  // If not a valid number or negative, set to empty
  if (!isNumber(enteredValue) || enteredValue < 0) {
    e.target.value = '';

    return e;
  }

  // Format with commas
  e.target.value = formatNumberWithCommas(Math.floor(enteredValue));

  return e;
};

const onlyFloatRegex: RegExp = /^[0-9]+(\.[0-9]{0,2})?$/;
const pickFloatRegex: RegExp = /[0-9]+(\.[0-9]{0,2})?/;

/* Transforms a textInput to a string representation of a float value.
 * We check if the whole input value is a float to two decimal places.
 *
 * This is NOT a pure function.
 * This WILL transform the provided event param.
 */
export const textInputToFloatMiddleware = (e: ChangeEvent<HTMLInputElement>) => {
  const eventValue = e.target.value;
  const isEntireInputFloat = onlyFloatRegex.test(eventValue);
  if (!isEntireInputFloat) {
    // The input is not in a two decimal float format.
    // Pick out the float portion.
    const matchedFloatValue = pickFloatRegex.exec(eventValue);
    e.target.value = matchedFloatValue === null ? '0' : matchedFloatValue[0];
  } else if (
    eventValue.length >= 2 &&
    eventValue.charAt(0) === '0' &&
    eventValue.charAt(1) !== '.'
  ) {
    // The input is a valid number but has a leading zero
    // Remove the leading zero.
    e.target.value = eventValue.substring(1);
  } else {
    // otherwise the entire input is a float and pass it through
    e.target.value = String(eventValue);
  }

  return e;
};

/* Transforms a textInput to a formatted float value with commas for the integer part.
 * This is NOT a pure function.
 * This WILL transform the provided event param.
 */
export const textInputToFormattedFloatMiddleware = (e: ChangeEvent<HTMLInputElement>) => {
  // Remove commas to get the raw value
  let rawValue = removeCommas(e.target.value);

  // If empty, allow it (user might be deleting)
  if (rawValue === '') {
    e.target.value = '';

    return e;
  }

  // Check if it's a valid float
  let isEntireInputFloat = onlyFloatRegex.test(rawValue);

  if (!isEntireInputFloat) {
    // The input is not in a two decimal float format.
    // Pick out the float portion.
    const matchedFloatValue = pickFloatRegex.exec(rawValue);
    if (matchedFloatValue === null) {
      e.target.value = '';

      return e;
    }
    const [matchedValue] = matchedFloatValue;
    rawValue = matchedValue;
    isEntireInputFloat = true;
  }

  // Handle leading zero case
  if (rawValue.length >= 2 && rawValue.charAt(0) === '0' && rawValue.charAt(1) !== '.') {
    rawValue = rawValue.substring(1);
  }

  // Split into integer and decimal parts
  const parts = rawValue.split('.');
  const integerPart = parts[0] || '0';
  const decimalPart = parts[1] || '';

  // Format integer part with commas
  const formattedInteger = formatNumberWithCommas(integerPart);

  // Reconstruct with decimal part if present
  e.target.value = decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;

  return e;
};
